import TRTC from 'trtc-sdk-v5'
import request from '@/utils/request'
import Message from '@/utils/message'
import { COURSE_TYPES } from './batch-id'

// LocalStorage key for persisting recording state
const STORAGE_KEY = 'trtc_recording_state'

// 状态常量
const STATE = {
  IDLE: 'IDLE',
  STARTING: 'STARTING',
  ROOM_JOINED: 'ROOM_JOINED',
  RECORDING: 'RECORDING',
  STOPPING: 'STOPPING'
}

class TrtcService {
  constructor () {
    this.trtc = null
    this.state = STATE.IDLE
    this.isRecording = false
    this.userId = null
    this.courseType = null
    this.recordId = null
    this.sessionNo = null
    this.captureMode = 'screen'
    this.roomId = null
    this.strRoomId = null
    this.startTime = null
    this.screenStream = null
    this.screenTrack = null
    this.screenAudioTrack = null
    this.micStream = null
    this.micTrack = null
    this.audioContext = null
    this.audioDestination = null
    this.mixedAudioTrack = null
    this.playbackElement = null
    this.playbackSourceNode = null
    this.playbackAudioAttached = false
    this._boundHandlers = {}

    // 尝试从 localStorage 恢复状态
    this.restoreState()
  }

  /**
   * 添加事件监听器
   */
  _addEventListeners () {
    if (!this.trtc) return

    this._removeEventListeners()

    this._boundHandlers.onEnterRoom = this._handleEnterRoom.bind(this)
    this._boundHandlers.onExitRoom = this._handleExitRoom.bind(this)
    this._boundHandlers.onError = this._handleError.bind(this)

    this.trtc.on('onEnterRoom', this._boundHandlers.onEnterRoom)
    this.trtc.on('onExitRoom', this._boundHandlers.onExitRoom)
    this.trtc.on('onError', this._boundHandlers.onError)
  }

  /**
   * 移除事件监听器
   */
  _removeEventListeners () {
    if (!this.trtc || !this._boundHandlers) return

    Object.keys(this._boundHandlers).forEach(eventName => {
      if (this._boundHandlers[eventName]) {
        this.trtc.off(eventName, this._boundHandlers[eventName])
      }
    })

    this._boundHandlers = {}
  }

  /**
   * 处理进房事件
   */
  _handleEnterRoom (result) {
    if (result > 0) {
      console.log('[TRTC] 进房成功，耗时:', result, 'ms')
    } else {
      console.error('[TRTC] 进房失败，错误码:', result)
    }
  }

  /**
   * 处理退房事件
   */
  _handleExitRoom (reason) {
    console.log('[TRTC] 退房，原因:', reason)
  }

  /**
   * 处理错误事件
   */
  _handleError (errCode, errMsg) {
    console.error('[TRTC] 错误:', errCode, errMsg)
  }

  _stopScreenTracks () {
    if (this.screenTrack) {
      this.screenTrack.stop()
      this.screenTrack = null
    }

    if (this.screenAudioTrack) {
      this.screenAudioTrack.stop()
      this.screenAudioTrack = null
    }

    if (this.screenStream) {
      this.screenStream.getTracks().forEach(track => track.stop())
      this.screenStream = null
    }
  }

  _stopAudioMix () {
    if (this.playbackSourceNode) {
      try {
        this.playbackSourceNode.disconnect()
      } catch (err) {
        // ignore disconnect errors
      }
      this.playbackSourceNode = null
    }

    if (this.mixedAudioTrack) {
      this.mixedAudioTrack.stop()
      this.mixedAudioTrack = null
    }

    if (this.audioDestination) {
      this.audioDestination = null
    }

    if (this.audioContext) {
      this.audioContext.close().catch(() => {})
      this.audioContext = null
    }

    this.playbackAudioAttached = false

    if (this.micTrack) {
      this.micTrack.stop()
      this.micTrack = null
    }

    if (this.micStream) {
      this.micStream.getTracks().forEach(track => track.stop())
      this.micStream = null
    }
  }

  _getLocalAudioTrack () {
    if (this.micTrack) {
      return this.micTrack
    }

    if (this.trtc && typeof this.trtc.getAudioTrack === 'function') {
      return this.trtc.getAudioTrack() || null
    }

    return null
  }

  _createMixedAudioTrack () {
    const hasScreenAudio = !!this.screenAudioTrack
    const micTrack = this._getLocalAudioTrack()
    const hasMicAudio = !!micTrack
    const hasPlaybackAudio = !!this.playbackElement

    if (!hasScreenAudio && !hasMicAudio && !hasPlaybackAudio) {
      return null
    }

    const audioGlobal = typeof window !== 'undefined' ? window : globalThis
    const AudioContextCtor = audioGlobal.AudioContext || audioGlobal.webkitAudioContext
    if (!AudioContextCtor) {
      return null
    }

    this.audioContext = new AudioContextCtor()
    this.audioDestination = this.audioContext.createMediaStreamDestination()

    if (this.screenAudioTrack) {
      const screenSource = this.audioContext.createMediaStreamSource(new MediaStream([this.screenAudioTrack]))
      screenSource.connect(this.audioDestination)
    }

    if (micTrack) {
      const micSource = this.audioContext.createMediaStreamSource(new MediaStream([micTrack]))
      micSource.connect(this.audioDestination)
    }

    if (this.playbackElement) {
      try {
        this.playbackSourceNode = this.audioContext.createMediaElementSource(this.playbackElement)
        this.playbackSourceNode.connect(this.audioDestination)
        this.playbackSourceNode.connect(this.audioContext.destination)
      } catch (err) {
        console.error('[TRTC] 连接页面播放音频失败:', err)
      }
    }

    const [mixedTrack] = this.audioDestination.stream.getAudioTracks()
    return mixedTrack || null
  }

  registerPlaybackElement (element) {
    if (!element) {
      return
    }

    if (!element.crossOrigin) {
      element.crossOrigin = 'anonymous'
    }

    this.playbackElement = element
  }

  async preparePlaybackCapture () {
    if (
      !this.trtc ||
      !this.playbackElement ||
      this.captureMode !== 'audio' ||
      (this.state !== STATE.ROOM_JOINED && this.state !== STATE.RECORDING)
    ) {
      return
    }

    if (!this.playbackAudioAttached) {
      const mixedTrack = this._createMixedAudioTrack()
      if (mixedTrack && typeof this.trtc.updateLocalAudio === 'function') {
        this.mixedAudioTrack = mixedTrack
        await this.trtc.updateLocalAudio({
          option: {
            audioTrack: mixedTrack
          }
        })
        this.playbackAudioAttached = true
      }
    }

    if (this.audioContext && this.audioContext.state === 'suspended' && typeof this.audioContext.resume === 'function') {
      await this.audioContext.resume()
    }
  }

  async _startScreenCaptureAsMain () {
    if (!navigator.mediaDevices || !navigator.mediaDevices.getDisplayMedia) {
      throw new Error('当前浏览器不支持屏幕共享')
    }

    this._stopScreenTracks()
    this._stopAudioMix()

    const stream = await navigator.mediaDevices.getDisplayMedia({
      video: {
        width: { ideal: 960 },
        frameRate: { ideal: 10, max: 10 }
      },
      audio: true
    })
    const [track] = stream.getVideoTracks()
    if (!track) {
      stream.getTracks().forEach(item => item.stop())
      throw new Error('未获取到屏幕轨道')
    }

    this.screenStream = stream
    this.screenTrack = track
    this.screenAudioTrack = stream.getAudioTracks()[0] || null
    this.screenTrack.onended = () => {
      console.log('[TRTC] 屏幕共享已结束')
    }

    const trackSettings = typeof track.getSettings === 'function' ? track.getSettings() : {}
    const profileWidth = Number.isFinite(trackSettings.width) ? trackSettings.width : 960
    const profileHeight = Number.isFinite(trackSettings.height)
      ? trackSettings.height
      : Math.round(profileWidth * 9 / 16)
    const profileFrameRate = Number.isFinite(trackSettings.frameRate)
      ? Math.min(trackSettings.frameRate, 10)
      : 10

    await this.trtc.startLocalVideo({
      publish: true,
      view: null,
      option: {
        videoTrack: track,
        profile: {
          width: profileWidth,
          height: profileHeight,
          frameRate: profileFrameRate,
          bitrate: 1200
        },
        qosPreference: TRTC.TYPE.QOS_PREFERENCE_CLEAR
      }
    })

    try {
      if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
        const micStream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false })
        const [micTrack] = micStream.getAudioTracks()
        this.micStream = micStream
        this.micTrack = micTrack || null
      }
    } catch (err) {
      console.error('[TRTC] 获取麦克风音频失败:', err)
    }

    const mixedTrack = this._createMixedAudioTrack()
    if (mixedTrack) {
      this.mixedAudioTrack = mixedTrack
      await this.trtc.startLocalAudio({
        publish: true,
        option: { audioTrack: mixedTrack }
      })
    } else {
      await this.trtc.startLocalAudio()
    }
  }

  async _stopScreenCaptureAsMain () {
    if (this.trtc) {
      try {
        await this.trtc.stopLocalVideo()
      } catch (err) {
        console.error('[TRTC] 停止主路视频失败:', err)
      }
    }
    this._stopAudioMix()
    this._stopScreenTracks()
  }

  /**
   * 从后端获取 UserSig
   * @param {String} courseType
   * @param {String|Number} recordId
   * @returns {Promise<Object>} { sdkAppId, userSig, privateMapKey, strRoomId }
   */
  async _fetchUserSig (courseType, recordId) {
    try {
      const response = await request.post('/trtc/user-sig', {
        courseType,
        recordId
      })

      if (response.error !== 0) {
        throw new Error(response.message || '获取 UserSig 失败')
      }

      return response.data
    } catch (err) {
      console.error('[TRTC] 获取 UserSig 失败:', err)
      throw new Error('获取 UserSig 失败: ' + (err.message || 'unknown error'))
    }
  }

  /**
   * 进入房间（事件驱动）
   * @param {Number} sdkAppId
   * @param {String} userSig
   * @param {String} privateMapKey
   * @returns {Promise<Number>} 进房耗时
   */
  async _enterRoom (sdkAppId, userSig, privateMapKey) {
    let timeoutId = null
    const params = {
      roomId: this.roomId,
      strRoomId: this.strRoomId,
      sdkAppId,
      userId: this.userId,
      userSig,
      privateMapKey,
      scene: 'rtc'
    }

    const timeoutPromise = new Promise((resolve, reject) => {
      timeoutId = setTimeout(() => {
        reject(new Error('进房超时'))
      }, 15000)
    })

    try {
      const result = await Promise.race([
        this.trtc.enterRoom(params),
        timeoutPromise
      ])
      console.log('[TRTC] 进房成功')
      return result
    } catch (err) {
      console.error('[TRTC] 进房失败:', err)
      throw err
    } finally {
      if (timeoutId) {
        clearTimeout(timeoutId)
      }
    }
  }

  /**
   * 保存录制状态到 localStorage
   */
  saveState () {
    if (this.isRecording) {
      const state = {
        userId: this.userId,
        courseType: this.courseType,
        recordId: this.recordId,
        sessionNo: this.sessionNo,
        captureMode: this.captureMode,
        roomId: this.roomId,
        strRoomId: this.strRoomId,
        startTime: this.startTime
      }
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state))
      console.log('[TRTC] 录制状态已保存到 localStorage')
    }
  }

  /**
   * 从 localStorage 恢复录制状态
   */
  restoreState () {
    try {
      const savedState = localStorage.getItem(STORAGE_KEY)
      if (savedState) {
        const state = JSON.parse(savedState)
        const elapsed = Date.now() - (state.startTime || 0)
        if (elapsed > 2 * 60 * 60 * 1000) {
          console.log('[TRTC] 检测到过期的录制状态，已清除')
          this.clearState()
          return
        }
        this.userId = state.userId
        this.courseType = state.courseType
        this.recordId = state.recordId
        this.sessionNo = state.sessionNo || null
        this.captureMode = state.captureMode || 'screen'
        this.roomId = state.roomId
        this.strRoomId = state.strRoomId
        this.startTime = state.startTime
        // 注意：不恢复 isRecording 和 trtc 实例，因为页面刷新后连接已断开
        console.log('[TRTC] 检测到未完成的录制会话:', state)
      }
    } catch (err) {
      console.error('[TRTC] 恢复录制状态失败:', err)
    }
  }

  /**
   * 清除保存的录制状态
   */
  clearState () {
    localStorage.removeItem(STORAGE_KEY)
    console.log('[TRTC] 录制状态已从 localStorage 清除')
  }

  /**
   * 检查是否有未完成的录制
   */
  hasPendingRecording () {
    return !!localStorage.getItem(STORAGE_KEY)
  }

  _assertCanStart () {
    if (this.state !== STATE.IDLE) {
      console.log('[TRTC] 录制已在进行中或正在启动，当前状态:', this.state)
      throw new Error(`无法启动录制，当前状态: ${this.state}`)
    }

    if (window.location.protocol === 'http:' && !['localhost', '127.0.0.1'].includes(window.location.hostname)) {
      const error = new Error('录制功能需要 HTTPS 环境。请使用 https:// 访问，或使用 localhost')
      error.code = 'REQUIRE_HTTPS'
      throw error
    }
  }

  async startWithCredentials (options = {}) {
    this._assertCanStart()

    const {
      sdkAppId,
      trtcUserId,
      userSig,
      privateMapKey,
      strRoomId,
      roomId = 0,
      captureMode = 'screen',
      sessionNo = null,
      courseType = null,
      recordId = null
    } = options

    if (!sdkAppId || !trtcUserId || !userSig || !privateMapKey || !strRoomId) {
      throw new Error('TRTC 凭证不完整')
    }

    this.state = STATE.STARTING
    this.userId = String(trtcUserId)
    this.courseType = courseType
    this.recordId = recordId == null ? null : String(recordId)
    this.sessionNo = sessionNo
    this.captureMode = captureMode
    this.roomId = Number(roomId || 0)
    this.strRoomId = strRoomId
    this.startTime = Date.now()

    console.log('[TRTC] 开始录制流程:', {
      userId: this.userId,
      courseType: this.courseType,
      recordId: this.recordId,
      sessionNo: this.sessionNo,
      captureMode: this.captureMode,
      state: this.state
    })

    try {
      this.trtc = TRTC.create()
      console.log('[TRTC] 实例已创建')

      this._addEventListeners()
      await this._enterRoom(sdkAppId, userSig, privateMapKey)
      this.state = STATE.ROOM_JOINED
      console.log('[TRTC] 进房成功，当前状态:', this.state)

      if (captureMode === 'audio') {
        await this.trtc.startLocalAudio()
        console.log('[TRTC] 纯音频模式已开启')
      } else {
        await this._startScreenCaptureAsMain()
        console.log('[TRTC] 屏幕共享已开启（主路）')
      }

      this.state = STATE.RECORDING
      this.isRecording = true
      this.saveState()

      console.log('[TRTC] 录制已成功开启')
      return this.getStatus()
    } catch (err) {
      console.error('[TRTC] 启动失败:', err)
      this.state = STATE.IDLE
      this.isRecording = false
      await this.cleanup()
      throw err
    }
  }

  /**
   * 开始录制
   * @param {String} courseType - 课程类型 (word/recite/reading)
   * @param {String|Number} recordId - 记录 ID (learnId 或 exerciseId)
   * @param {Object} options - 可选参数
   * @param {String} options.userId - 用户 ID（如不传则从 vuex 获取）
   */
  async start (courseType, recordId, options = {}) {
    this._assertCanStart()

    // 校验参数
    if (!courseType || !recordId) {
      console.error('[TRTC] 参数错误: courseType 或 recordId 为空')
      throw new Error('courseType 和 recordId 不能为空')
    }

    // 获取用户 ID
    const userId = options.userId
    if (!userId) {
      console.error('[TRTC] 用户 ID 为空')
      throw new Error('用户 ID 不能为空')
    }

    const credentials = await this._fetchUserSig(courseType, recordId)
    return this.startWithCredentials({
      sdkAppId: credentials.sdkAppId,
      trtcUserId: String(userId),
      userSig: credentials.userSig,
      privateMapKey: credentials.privateMapKey,
      strRoomId: credentials.strRoomId,
      roomId: 0,
      captureMode: options.captureMode || 'screen',
      sessionNo: options.sessionNo || null,
      courseType,
      recordId
    })
  }

  /**
   * 停止录制
   */
  async stop (options = {}) {
    if (this.state !== STATE.RECORDING) {
      console.log('[TRTC] 当前未在录制中，状态:', this.state)
      return
    }

    this.state = STATE.STOPPING
    console.log('[TRTC] 开始停止录制')

    try {
      if (this.captureMode === 'screen') {
        await this._stopScreenCaptureAsMain()
        console.log('[TRTC] 屏幕共享已停止（主路）')
      } else {
        this._stopAudioMix()
      }
    } catch (err) {
      console.error('[TRTC] 停止屏幕共享失败:', err)
    }

    try {
      await this.trtc.stopLocalAudio()
      console.log('[TRTC] 麦克风已关闭')
    } catch (err) {
      console.error('[TRTC] 停止麦克风失败:', err)
    }

    try {
      await this.trtc.exitRoom()
      console.log('[TRTC] 已退出房间')
    } catch (err) {
      console.error('[TRTC] 退出房间失败:', err)
    }

    await this.cleanup()
    if (!options.silent) {
      Message.success('完成并提交')
    }
  }

  /**
   * 清理资源
   */
  async cleanup () {
    console.log('[TRTC] 清理资源，当前状态:', this.state)

    this._removeEventListeners()

    if (this.captureMode === 'screen') {
      await this._stopScreenCaptureAsMain()
    } else {
      this._stopAudioMix()
    }

    if (this.trtc) {
      try {
        // 必须先调用 exitRoom() 再调用 destroy()
        // 只有在已进房的情况下才需要退房
        if (this.state === STATE.ROOM_JOINED || this.state === STATE.RECORDING || this.state === STATE.STOPPING) {
          await this.trtc.exitRoom()
          console.log('[TRTC] cleanup: 已退出房间')
        }
      } catch (err) {
        console.error('[TRTC] cleanup: 退出房间失败:', err)
      }

      try {
        this.trtc.destroy()
        console.log('[TRTC] cleanup: 已销毁实例')
      } catch (err) {
        console.error('[TRTC] cleanup: 销毁实例失败:', err)
      }

      this.trtc = null
    }

    this.state = STATE.IDLE
    this.isRecording = false
    this.userId = null
    this.courseType = null
    this.recordId = null
    this.sessionNo = null
    this.captureMode = 'screen'
    this.roomId = null
    this.strRoomId = null
    this.startTime = null
    this.playbackElement = null

    // 清除 localStorage 中的录制状态
    this.clearState()

    console.log('[TRTC] 资源清理完成')
  }

  /**
   * 获取录制状态
   */
  getStatus () {
    return {
      isRecording: this.isRecording,
      userId: this.userId,
      courseType: this.courseType,
      recordId: this.recordId,
      sessionNo: this.sessionNo,
      captureMode: this.captureMode,
      roomId: this.roomId,
      strRoomId: this.strRoomId,
      startTime: this.startTime
    }
  }
}

// 单例模式
let instance = null

/**
 * 获取 TRTC 服务实例
 * @returns {TrtcService}
 */
export const getTrtcService = () => {
  if (!instance) {
    instance = new TrtcService()
  }
  return instance
}

// 导出课程类型常量
export { COURSE_TYPES }

export default TrtcService
